import { subject } from '@casl/ability';
import {
    ChartSourceType,
    ContentType,
    DashboardTileTypes,
    FeatureFlags,
    ResourceViewItemType,
    type ResourceViewChartItem,
} from '@lightdash/common';
import {
    ActionIcon,
    Box,
    Button,
    Group,
    Menu,
    Text,
    Title,
    Tooltip,
} from '@mantine-8/core';
import { useDisclosure } from '@mantine-8/hooks';
import {
    IconAlertCircle,
    IconArrowBack,
    IconArrowsExchange,
    IconBell,
    IconCircleCheck,
    IconCircleCheckFilled,
    IconCirclesRelation,
    IconCopy,
    IconDatabaseExport,
    IconDots,
    IconFolders,
    IconFolderSymlink,
    IconHistory,
    IconLayoutGridAdd,
    IconPencil,
    IconPin,
    IconPinnedOff,
    IconSend,
    IconStar,
    IconStarFilled,
    IconTrash,
} from '@tabler/icons-react';
import {
    lazy,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
} from 'react';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import { AskAiAgentMenuItem } from '../../../ee/features/aiCopilot/components/AskAiAgentMenuItem/AskAiAgentMenuItem';
import {
    explorerActions,
    selectHasUnsavedChanges,
    selectIsEditMode,
    selectIsValidQuery,
    selectSavedChart,
    selectUnsavedChartVersion,
    useExplorerDispatch,
    useExplorerSelector,
} from '../../../features/explorer/store';
import { PromotionConfirmDialog } from '../../../features/promotion/components/PromotionConfirmDialog';
import {
    usePromoteChartDiffMutation,
    usePromoteMutation,
} from '../../../features/promotion/hooks/usePromoteChart';
import { ChartSchedulersModal } from '../../../features/scheduler';
import {
    getSchedulerUuidFromUrlParams,
    getThresholdUuidFromUrlParams,
    isSchedulerTypeSync,
} from '../../../features/scheduler/utils';
import { SyncModal as GoogleSheetsSyncModal } from '../../../features/sync/components';
import { useChartViewStats } from '../../../hooks/chart/useChartViewStats';
import useDashboardStorage from '../../../hooks/dashboard/useDashboardStorage';
import { useFavoriteMutation } from '../../../hooks/favorites/useFavoriteMutation';
import { useFavorites } from '../../../hooks/favorites/useFavorites';
import { useChartPinningMutation } from '../../../hooks/pinning/useChartPinningMutation';
import { useContentAction } from '../../../hooks/useContent';
import {
    useUnverifyChartMutation,
    useVerifyChartMutation,
} from '../../../hooks/useContentVerification';
import { useExplorerQuery } from '../../../hooks/useExplorerQuery';
import { useProject } from '../../../hooks/useProject';
import { useUpdateMutation } from '../../../hooks/useSavedQuery';
import useSearchParams from '../../../hooks/useSearchParams';
import { useServerFeatureFlag } from '../../../hooks/useServerOrClientFeatureFlag';
import { Can } from '../../../providers/Ability';
import useApp from '../../../providers/App/useApp';
import {
    defaultQueryExecution,
    defaultState,
} from '../../../providers/Explorer/defaultState';
import { ExplorerSection } from '../../../providers/Explorer/types';
import { TrackSection } from '../../../providers/Tracking/TrackingProvider';
import { SectionName } from '../../../types/Events';
import MantineIcon from '../../common/MantineIcon';
import MantineModal from '../../common/MantineModal';
const ChangeChartExploreModal = lazy(
    () => import('../../common/modal/ChangeChartExploreModal'),
);
import ChartDeleteModal from '../../common/modal/ChartDeleteModal';
import ChartDuplicateModal from '../../common/modal/ChartDuplicateModal';
import ChartUpdateModal from '../../common/modal/ChartUpdateModal';
import MoveChartThatBelongsToDashboardModal from '../../common/modal/MoveChartThatBelongsToDashboardModal';
import PageHeader from '../../common/Page/PageHeader';
import { UpdatedInfo } from '../../common/PageHeader/UpdatedInfo';
import { ResourceInfoPopup } from '../../common/ResourceInfoPopup/ResourceInfoPopup';
import ShareShortLinkButton from '../../common/ShareShortLinkButton';
import TransferItemsModal from '../../common/TransferItemsModal/TransferItemsModal';
import ExploreFromHereButton from '../../ExploreFromHereButton';
import AddTilesToDashboardModal from '../../SavedDashboards/AddTilesToDashboardModal';
import SaveChartButton from '../SaveChartButton';
import { TitleBreadCrumbs } from './TitleBreadcrumbs';

const SavedChartsHeader: FC = () => {
    const { data: changeChartExploreFlag } = useServerFeatureFlag(
        FeatureFlags.ChangeChartExplore,
    );
    const changeChartExploreEnabled = changeChartExploreFlag?.enabled === true;

    const { search, pathname } = useLocation();
    const { projectUuid } = useParams<{
        projectUuid: string;
    }>();
    const dashboardUuid = useSearchParams('fromDashboard');
    const isFromDashboard = !!dashboardUuid;

    const { data: project } = useProject(projectUuid);

    const { mutate: promoteChart } = usePromoteMutation();
    const {
        mutate: getPromoteChartDiff,
        data: promoteChartDiff,
        reset: resetPromoteChartDiff,
        isLoading: promoteChartDiffLoading,
    } = usePromoteChartDiffMutation();
    const navigate = useNavigate();
    const dispatch = useExplorerDispatch();

    const isEditMode = useExplorerSelector(selectIsEditMode);
    const unsavedChartVersion = useExplorerSelector(selectUnsavedChartVersion);

    const savedChart = useExplorerSelector(selectSavedChart);

    const hasUnsavedChanges = useExplorerSelector(selectHasUnsavedChanges);

    const { query } = useExplorerQuery();
    const itemsMap = query.data?.fields;

    const isValidQuery = useExplorerSelector(selectIsValidQuery);

    const isPinned = useMemo(() => {
        return Boolean(savedChart?.pinnedListUuid);
    }, [savedChart?.pinnedListUuid]);
    const { mutate: togglePinChart } = useChartPinningMutation();
    const onChartPinning = useCallback(() => {
        if (!savedChart) return;
        togglePinChart({ uuid: savedChart.uuid });
    }, [savedChart, togglePinChart]);

    const { data: favorites } = useFavorites(projectUuid);
    const { mutate: toggleFavorite } = useFavoriteMutation(projectUuid);
    const isChartFavorited = useMemo(
        () => favorites?.some((f) => f.data.uuid === savedChart?.uuid) ?? false,
        [favorites, savedChart?.uuid],
    );

    const { clearDashboardStorage } = useDashboardStorage();
    const [isRenamingChart, setIsRenamingChart] = useState(false);
    const [isMovingChart, setIsMovingChart] = useState(false);
    const [isDeleteModalOpen, deleteModalHandlers] = useDisclosure();
    const [isScheduledDeliveriesModalOpen, scheduledDeliveriesModalHandlers] =
        useDisclosure();
    const [isThresholdAlertsModalOpen, thresholdAlertsModalHandlers] =
        useDisclosure();
    const [isSyncWithGoogleSheetsModalOpen, syncWithGoogleSheetsModalHandlers] =
        useDisclosure();
    const [isAddToDashboardModalOpen, addToDashboardModalHandlers] =
        useDisclosure();
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isChartDuplicateModalOpen, chartDuplicateModalHandlers] =
        useDisclosure();
    const [isChangeExploreModalOpen, changeExploreModalHandlers] =
        useDisclosure();
    const [isTransferToSpaceModalOpen, transferToSpaceModalHandlers] =
        useDisclosure();

    const { user, health } = useApp();
    const { mutateAsync: contentAction, isLoading: isContentActionLoading } =
        useContentAction(projectUuid);
    const updateSavedChart = useUpdateMutation(
        dashboardUuid ? dashboardUuid : undefined,
        savedChart?.uuid,
    );
    const chartViewStats = useChartViewStats(savedChart?.uuid);
    const chartBelongsToDashboard: boolean = !!savedChart?.dashboardUuid;

    const hasGoogleDriveEnabled =
        health.data?.auth.google.oauth2ClientId !== undefined &&
        health.data?.auth.google.googleDriveApiKey !== undefined;

    // Capture scheduler UUID from URL for deep linking to edit mode
    const [initialSchedulerUuid, setInitialSchedulerUuid] = useState<
        string | undefined
    >(() => getSchedulerUuidFromUrlParams(search) ?? undefined);
    const [initialThresholdUuid, setInitialThresholdUuid] = useState<
        string | undefined
    >(() => getThresholdUuidFromUrlParams(search) ?? undefined);

    const hasProcessedUrlParams = useRef(false);
    useEffect(() => {
        if (hasProcessedUrlParams.current) return;

        const schedulerUuidFromUrlParams =
            getSchedulerUuidFromUrlParams(search);
        const thresholdUuidFromUrlParams =
            getThresholdUuidFromUrlParams(search);

        if (!schedulerUuidFromUrlParams && !thresholdUuidFromUrlParams) {
            return;
        }

        hasProcessedUrlParams.current = true;

        const isSync = isSchedulerTypeSync(search);
        if (schedulerUuidFromUrlParams) {
            if (isSync) {
                syncWithGoogleSheetsModalHandlers.open();
            } else {
                scheduledDeliveriesModalHandlers.open();
            }
        } else if (thresholdUuidFromUrlParams) {
            thresholdAlertsModalHandlers.open();
        }

        // Clear URL params to prevent modal from reopening on close
        const newParams = new URLSearchParams(search);
        newParams.delete('scheduler_uuid');
        newParams.delete('threshold_uuid');
        newParams.delete('isSync');
        void navigate(
            { pathname, search: newParams.toString() },
            { replace: true },
        );
    }, [
        search,
        navigate,
        pathname,
        syncWithGoogleSheetsModalHandlers,
        scheduledDeliveriesModalHandlers,
        thresholdAlertsModalHandlers,
    ]);

    // Clear initial UUIDs when modals are closed so reopening shows the list
    const wasScheduledDeliveriesModalOpen = useRef(false);
    useEffect(() => {
        // Only clear when transitioning from open to closed, not on initial render
        if (
            wasScheduledDeliveriesModalOpen.current &&
            !isScheduledDeliveriesModalOpen
        ) {
            setInitialSchedulerUuid(undefined);
        }
        wasScheduledDeliveriesModalOpen.current =
            isScheduledDeliveriesModalOpen;
    }, [isScheduledDeliveriesModalOpen]);

    const wasThresholdAlertsModalOpen = useRef(false);
    useEffect(() => {
        // Only clear when transitioning from open to closed, not on initial render
        if (
            wasThresholdAlertsModalOpen.current &&
            !isThresholdAlertsModalOpen
        ) {
            setInitialThresholdUuid(undefined);
        }
        wasThresholdAlertsModalOpen.current = isThresholdAlertsModalOpen;
    }, [isThresholdAlertsModalOpen]);

    useEffect(() => {
        const checkReload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges && isEditMode) {
                const message =
                    'You have unsaved changes to your dashboard! Are you sure you want to leave without saving?';
                event.returnValue = message;
                return message;
            }
        };
        window.addEventListener('beforeunload', checkReload);
        return () => window.removeEventListener('beforeunload', checkReload);
    }, [hasUnsavedChanges, isEditMode]);

    // Block navigating away if there are unsaved changes
    const blocker = useBlocker(({ nextLocation }) => {
        if (
            hasUnsavedChanges &&
            isEditMode &&
            !isSaveModalOpen &&
            !nextLocation.pathname.includes(
                `/projects/${projectUuid}/saved/${savedChart?.uuid}`,
            ) &&
            !nextLocation.pathname.includes(
                `/projects/${projectUuid}/dashboards/${dashboardUuid}`,
            )
        ) {
            return true; //blocks navigation
        }
        return false; // allow navigation
    });

    const userCanManageChart =
        savedChart &&
        user.data?.ability?.can(
            'manage',
            subject('SavedChart', { ...savedChart }),
        );

    // Mirror the backend promote check: promoting also requires promote
    // rights on the upstream (destination) project, so hide the action when
    // an upstream exists but the user has no promote access there.
    const userCanPromoteChart =
        savedChart &&
        !savedChart?.dashboardUuid &&
        user.data?.ability?.can(
            'promote',
            subject('SavedChart', { ...savedChart }),
        ) &&
        (project?.upstreamProjectUuid === undefined ||
            user.data?.ability?.can(
                'promote',
                subject('SavedChart', {
                    organizationUuid: savedChart.organizationUuid,
                    projectUuid: project.upstreamProjectUuid,
                }),
            ));

    const userCanManageExplore = user.data?.ability.can(
        'manage',
        subject('Explore', {
            organizationUuid: user.data?.organizationUuid,
            projectUuid: savedChart?.projectUuid,
        }),
    );

    const userCanCreateDeliveriesAndAlerts = user.data?.ability?.can(
        'create',
        subject('ScheduledDeliveries', {
            organizationUuid: user.data?.organizationUuid,
            projectUuid,
        }),
    );

    const canManageContentVerification =
        user.data?.ability?.can(
            'manage',
            subject('ContentVerification', {
                organizationUuid: user.data?.organizationUuid,
                projectUuid,
            }),
        ) === true;

    const { mutate: verifyChart } = useVerifyChartMutation();
    const { mutate: unverifyChart } = useUnverifyChartMutation();

    const isChartVerified =
        savedChart?.verification !== null &&
        savedChart?.verification !== undefined;

    const userCanPinChart = user.data?.ability.can(
        'manage',
        subject('PinnedItems', {
            organizationUuid: user.data?.organizationUuid,
            projectUuid,
        }),
    );

    const handleGoBackClick = () => {
        void navigate({
            pathname: `/projects/${savedChart?.projectUuid}/dashboards/${dashboardUuid}`,
        });
    };

    const handleCancelClick = useCallback(() => {
        // Reset to saved chart state
        if (savedChart) {
            const resetState = {
                savedChart,
                isEditMode,
                parameterReferences: Object.keys(savedChart.parameters ?? {}),
                parameterDefinitions: {},
                cachedChartConfigs: {},
                expandedSections: [ExplorerSection.VISUALIZATION],
                unsavedChartVersion: {
                    tableName: savedChart.tableName,
                    chartConfig: savedChart.chartConfig,
                    metricQuery: savedChart.metricQuery,
                    tableConfig: savedChart.tableConfig,
                    pivotConfig: savedChart.pivotConfig,
                    parameters: savedChart.parameters,
                },
                unsavedColorPaletteUuid: savedChart.colorPaletteUuid,
                modals: defaultState.modals,
                queryExecution: defaultQueryExecution,
                preAggregate: defaultState.preAggregate,
            };
            dispatch(explorerActions.reset(resetState));
        }

        if (!isFromDashboard)
            void navigate({
                pathname: `/projects/${savedChart?.projectUuid}/saved/${savedChart?.uuid}/view`,
            });
    }, [dispatch, isEditMode, savedChart, isFromDashboard, navigate]);

    const promoteDisabled = !(
        project?.upstreamProjectUuid !== undefined && userCanPromoteChart
    );

    return (
        <TrackSection name={SectionName.EXPLORER_TOP_BUTTONS}>
            {blocker.state === 'blocked' && (
                <MantineModal
                    opened
                    onClose={() => {
                        blocker.reset();
                    }}
                    title="Unsaved changes"
                    icon={IconAlertCircle}
                    cancelLabel="Stay"
                    actions={
                        <Button
                            color="red"
                            onClick={() => {
                                blocker.proceed();
                            }}
                        >
                            Leave
                        </Button>
                    }
                >
                    <Text fw={500}>
                        You have unsaved changes to your chart! Are you sure you
                        want to leave without saving?
                    </Text>
                </MantineModal>
            )}

            <PageHeader
                cardProps={{
                    py: 'xs',
                }}
            >
                <div style={{ flex: 1 }}>
                    {savedChart && projectUuid && (
                        <>
                            <Group gap={4}>
                                <TitleBreadCrumbs
                                    projectUuid={projectUuid}
                                    spaceUuid={savedChart.spaceUuid}
                                    spaceName={savedChart.spaceName}
                                    dashboardUuid={savedChart.dashboardUuid}
                                    dashboardName={savedChart.dashboardName}
                                />
                                <Title
                                    c="ldDark.9"
                                    order={5}
                                    fw={600}
                                    maw={500}
                                    lineClamp={1}
                                >
                                    {savedChart.name}
                                </Title>

                                {isChartVerified && (
                                    <Tooltip
                                        label={
                                            savedChart?.verification?.verifiedBy
                                                ? `Verified by ${savedChart.verification.verifiedBy.firstName} ${savedChart.verification.verifiedBy.lastName}`
                                                : 'Verified'
                                        }
                                        withArrow
                                        withinPortal
                                        zIndex={10000}
                                    >
                                        <IconCircleCheckFilled
                                            size={16}
                                            style={{
                                                color: 'var(--mantine-color-green-6)',
                                            }}
                                        />
                                    </Tooltip>
                                )}

                                <ActionIcon
                                    size="xs"
                                    variant="transparent"
                                    color={
                                        isChartFavorited ? 'orange' : 'ldGray.6'
                                    }
                                    onClick={() => {
                                        toggleFavorite({
                                            contentType: ContentType.CHART,
                                            contentUuid: savedChart.uuid,
                                        });
                                    }}
                                >
                                    {isChartFavorited ? (
                                        <IconStarFilled size={16} />
                                    ) : (
                                        <IconStar size={16} />
                                    )}
                                </ActionIcon>

                                {isEditMode && userCanManageChart && (
                                    <ActionIcon
                                        size="xs"
                                        color="ldGray.6"
                                        disabled={updateSavedChart.isLoading}
                                        onClick={() => setIsRenamingChart(true)}
                                    >
                                        <MantineIcon icon={IconPencil} />
                                    </ActionIcon>
                                )}
                            </Group>
                            <ChartUpdateModal
                                opened={isRenamingChart}
                                uuid={savedChart.uuid}
                                onClose={() => setIsRenamingChart(false)}
                                onConfirm={() => setIsRenamingChart(false)}
                            />
                            <Group gap="xs">
                                <UpdatedInfo
                                    updatedAt={savedChart.updatedAt}
                                    user={savedChart.updatedByUser}
                                    partiallyBold={false}
                                />
                                <ResourceInfoPopup
                                    resourceUuid={savedChart.uuid}
                                    projectUuid={projectUuid}
                                    title={savedChart.name}
                                    description={savedChart.description}
                                    slug={savedChart.slug}
                                    updatedAt={savedChart.updatedAt}
                                    spaceName={savedChart.spaceName}
                                    spaceUuid={savedChart.spaceUuid}
                                    viewStats={chartViewStats.data?.views}
                                    firstViewedAt={
                                        chartViewStats.data?.firstViewedAt
                                    }
                                    withChartData={true}
                                />
                            </Group>
                        </>
                    )}
                </div>
                {(userCanManageChart ||
                    userCanCreateDeliveriesAndAlerts ||
                    userCanManageExplore) && (
                    <Group gap="xs">
                        {userCanManageExplore && !isEditMode && (
                            <ExploreFromHereButton />
                        )}
                        {userCanManageChart && (
                            <>
                                {/* TODO: Extract this into a separate component, depending on the mode: viewing or editing */}
                                {!isEditMode ? (
                                    <>
                                        <Button
                                            variant="default"
                                            size="xs"
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconPencil}
                                                />
                                            }
                                            onClick={() =>
                                                navigate({
                                                    pathname: `/projects/${savedChart?.projectUuid}/saved/${savedChart?.uuid}/edit`,
                                                })
                                            }
                                        >
                                            Edit chart
                                        </Button>
                                        <ShareShortLinkButton
                                            disabled={!isValidQuery}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <SaveChartButton
                                            onSaveModalOpenChange={
                                                setIsSaveModalOpen
                                            }
                                        />
                                        <Button
                                            variant="default"
                                            size="xs"
                                            disabled={
                                                isFromDashboard &&
                                                !hasUnsavedChanges
                                            }
                                            onClick={handleCancelClick}
                                        >
                                            Cancel{' '}
                                            {isFromDashboard ? 'changes' : ''}
                                        </Button>

                                        {isFromDashboard && (
                                            <Tooltip
                                                offset={-1}
                                                label="Return to dashboard"
                                                withinPortal
                                                position="bottom"
                                            >
                                                <ActionIcon
                                                    variant="default"
                                                    onClick={handleGoBackClick}
                                                >
                                                    <MantineIcon
                                                        icon={IconArrowBack}
                                                    />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {/* TODO: Refactor this into its own component */}
                        <Menu
                            position="bottom"
                            withArrow
                            withinPortal
                            shadow="md"
                            width={200}
                            disabled={!unsavedChartVersion.tableName}
                        >
                            <Menu.Dropdown>
                                {savedChart && (
                                    <AskAiAgentMenuItem
                                        projectUuid={projectUuid}
                                        chartUuid={savedChart.uuid}
                                        clickedFrom="saved_chart_header"
                                        withDivider
                                    />
                                )}
                                <Menu.Label>Manage</Menu.Label>
                                {userCanManageChart &&
                                    !hasUnsavedChanges &&
                                    !chartBelongsToDashboard && (
                                        <Menu.Item
                                            leftSection={
                                                <MantineIcon icon={IconCopy} />
                                            }
                                            onClick={
                                                chartDuplicateModalHandlers.open
                                            }
                                        >
                                            Duplicate
                                        </Menu.Item>
                                    )}
                                {userCanManageChart &&
                                    !chartBelongsToDashboard && (
                                        <Menu.Item
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconLayoutGridAdd}
                                                />
                                            }
                                            onClick={
                                                addToDashboardModalHandlers.open
                                            }
                                        >
                                            Add to dashboard
                                        </Menu.Item>
                                    )}
                                {userCanManageChart &&
                                    savedChart?.dashboardUuid && (
                                        <Menu.Item
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconFolders}
                                                />
                                            }
                                            onClick={() =>
                                                setIsMovingChart(true)
                                            }
                                        >
                                            Move to space
                                        </Menu.Item>
                                    )}

                                {!chartBelongsToDashboard &&
                                    userCanPinChart && (
                                        <Menu.Item
                                            component="button"
                                            role="menuitem"
                                            leftSection={
                                                isPinned ? (
                                                    <MantineIcon
                                                        icon={IconPinnedOff}
                                                    />
                                                ) : (
                                                    <MantineIcon
                                                        icon={IconPin}
                                                    />
                                                )
                                            }
                                            onClick={onChartPinning}
                                        >
                                            {isPinned
                                                ? 'Unpin from homepage'
                                                : 'Pin to homepage'}
                                        </Menu.Item>
                                    )}

                                {userCanManageChart &&
                                    !chartBelongsToDashboard && (
                                        <Menu.Item
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconFolderSymlink}
                                                />
                                            }
                                            onClick={
                                                transferToSpaceModalHandlers.open
                                            }
                                        >
                                            Move chart
                                        </Menu.Item>
                                    )}

                                {userCanManageChart && (
                                    <Menu.Item
                                        leftSection={
                                            <MantineIcon icon={IconHistory} />
                                        }
                                        onClick={() =>
                                            navigate({
                                                pathname: `/projects/${savedChart?.projectUuid}/saved/${savedChart?.uuid}/history`,
                                            })
                                        }
                                    >
                                        Version history
                                    </Menu.Item>
                                )}
                                {changeChartExploreEnabled &&
                                    userCanManageChart && (
                                        <Menu.Item
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconArrowsExchange}
                                                />
                                            }
                                            onClick={
                                                changeExploreModalHandlers.open
                                            }
                                        >
                                            Change explore
                                        </Menu.Item>
                                    )}
                                {userCanPromoteChart && (
                                    <Tooltip
                                        label="You must enable first an upstream project in settings > Data ops"
                                        disabled={!promoteDisabled}
                                        withinPortal
                                    >
                                        <div>
                                            <Menu.Item
                                                disabled={promoteDisabled}
                                                leftSection={
                                                    <MantineIcon
                                                        icon={
                                                            IconDatabaseExport
                                                        }
                                                    />
                                                }
                                                onClick={() => {
                                                    if (savedChart)
                                                        getPromoteChartDiff(
                                                            savedChart?.uuid,
                                                        );
                                                }}
                                            >
                                                Promote chart
                                            </Menu.Item>
                                        </div>
                                    </Tooltip>
                                )}

                                {canManageContentVerification &&
                                    savedChart?.uuid && (
                                        <Menu.Item
                                            leftSection={
                                                isChartVerified ? (
                                                    <IconCircleCheckFilled
                                                        size={18}
                                                        color="var(--mantine-color-green-6)"
                                                    />
                                                ) : (
                                                    <IconCircleCheck
                                                        size={18}
                                                    />
                                                )
                                            }
                                            onClick={() => {
                                                if (isChartVerified) {
                                                    unverifyChart(
                                                        savedChart.uuid,
                                                    );
                                                } else {
                                                    verifyChart(
                                                        savedChart.uuid,
                                                    );
                                                }
                                            }}
                                        >
                                            {isChartVerified
                                                ? 'Remove verification'
                                                : 'Verify'}
                                        </Menu.Item>
                                    )}

                                <Menu.Divider />
                                <Menu.Label>Integrations</Menu.Label>
                                {userCanCreateDeliveriesAndAlerts && (
                                    <Menu.Item
                                        leftSection={
                                            <MantineIcon icon={IconSend} />
                                        }
                                        onClick={
                                            scheduledDeliveriesModalHandlers.open
                                        }
                                    >
                                        Scheduled deliveries
                                    </Menu.Item>
                                )}
                                {userCanCreateDeliveriesAndAlerts && (
                                    <Menu.Item
                                        leftSection={
                                            <MantineIcon icon={IconBell} />
                                        }
                                        onClick={
                                            thresholdAlertsModalHandlers.open
                                        }
                                    >
                                        Alerts
                                    </Menu.Item>
                                )}
                                {userCanManageChart &&
                                    hasGoogleDriveEnabled &&
                                    userCanCreateDeliveriesAndAlerts && (
                                        <Can
                                            I="manage"
                                            this={subject('GoogleSheets', {
                                                organizationUuid:
                                                    user.data?.organizationUuid,
                                                projectUuid,
                                            })}
                                        >
                                            <Menu.Item
                                                leftSection={
                                                    <MantineIcon
                                                        icon={
                                                            IconCirclesRelation
                                                        }
                                                    />
                                                }
                                                onClick={
                                                    syncWithGoogleSheetsModalHandlers.open
                                                }
                                            >
                                                Google Sheets Sync
                                            </Menu.Item>
                                        </Can>
                                    )}

                                {userCanManageChart && (
                                    <>
                                        <Menu.Divider />

                                        <Box>
                                            <Menu.Item
                                                leftSection={
                                                    <MantineIcon
                                                        icon={IconTrash}
                                                        color="red"
                                                    />
                                                }
                                                color="red"
                                                onClick={
                                                    deleteModalHandlers.open
                                                }
                                            >
                                                Delete
                                            </Menu.Item>
                                        </Box>
                                    </>
                                )}
                            </Menu.Dropdown>
                            <Menu.Target>
                                <ActionIcon
                                    variant="default"
                                    disabled={!unsavedChartVersion.tableName}
                                >
                                    <MantineIcon icon={IconDots} />
                                </ActionIcon>
                            </Menu.Target>
                        </Menu>
                    </Group>
                )}
            </PageHeader>

            {savedChart && isAddToDashboardModalOpen && projectUuid && (
                <AddTilesToDashboardModal
                    isOpen={isAddToDashboardModalOpen}
                    projectUuid={projectUuid}
                    uuid={savedChart.uuid}
                    dashboardTileType={DashboardTileTypes.SAVED_CHART}
                    onClose={addToDashboardModalHandlers.close}
                />
            )}
            {isDeleteModalOpen && savedChart?.uuid && (
                <ChartDeleteModal
                    uuid={savedChart.uuid}
                    opened={isDeleteModalOpen}
                    onClose={deleteModalHandlers.close}
                    onConfirm={() => {
                        if (dashboardUuid) {
                            void navigate(
                                `/projects/${projectUuid}/dashboards/${dashboardUuid}`,
                            );
                        } else {
                            void navigate(`/`);
                        }
                        clearDashboardStorage();
                        deleteModalHandlers.close();
                    }}
                />
            )}
            {isSyncWithGoogleSheetsModalOpen && savedChart?.uuid && (
                <GoogleSheetsSyncModal
                    chartUuid={savedChart.uuid}
                    opened={isSyncWithGoogleSheetsModalOpen}
                    onClose={syncWithGoogleSheetsModalHandlers.close}
                />
            )}
            {isScheduledDeliveriesModalOpen && savedChart?.uuid && (
                <ChartSchedulersModal
                    chartUuid={savedChart.uuid}
                    name={savedChart.name}
                    isOpen={isScheduledDeliveriesModalOpen}
                    onClose={scheduledDeliveriesModalHandlers.close}
                    initialSchedulerUuid={initialSchedulerUuid}
                />
            )}
            {isThresholdAlertsModalOpen && savedChart?.uuid && (
                <ChartSchedulersModal
                    chartUuid={savedChart.uuid}
                    name={savedChart.name}
                    isThresholdAlert
                    itemsMap={itemsMap}
                    isOpen={isThresholdAlertsModalOpen}
                    onClose={thresholdAlertsModalHandlers.close}
                    initialSchedulerUuid={initialThresholdUuid}
                />
            )}
            {savedChart && (
                <MoveChartThatBelongsToDashboardModal
                    className={'non-draggable'}
                    projectUuid={projectUuid}
                    uuid={savedChart.uuid}
                    name={savedChart.name}
                    spaceUuid={savedChart.spaceUuid}
                    spaceName={savedChart.spaceName}
                    opened={isMovingChart}
                    onClose={() => setIsMovingChart(false)}
                    onConfirm={() => {
                        clearDashboardStorage();
                        void navigate(
                            `/projects/${projectUuid}/saved/${savedChart.uuid}/edit`,
                        );
                    }}
                />
            )}

            {isChartDuplicateModalOpen && savedChart?.uuid && (
                <ChartDuplicateModal
                    opened={isChartDuplicateModalOpen}
                    uuid={savedChart.uuid}
                    onClose={chartDuplicateModalHandlers.close}
                    onConfirm={chartDuplicateModalHandlers.close}
                />
            )}

            {(promoteChartDiff || promoteChartDiffLoading) && (
                <PromotionConfirmDialog
                    type={'chart'}
                    resourceName={savedChart?.name ?? ''}
                    promotionChanges={promoteChartDiff}
                    onClose={() => {
                        resetPromoteChartDiff();
                    }}
                    onConfirm={() => {
                        if (savedChart?.uuid) promoteChart(savedChart.uuid);
                    }}
                />
            )}

            {isTransferToSpaceModalOpen && projectUuid && (
                <TransferItemsModal
                    projectUuid={projectUuid}
                    opened={isTransferToSpaceModalOpen}
                    items={[
                        ...(savedChart && chartViewStats.data
                            ? [
                                  {
                                      data: {
                                          ...savedChart,
                                          firstViewedAt:
                                              chartViewStats.data.firstViewedAt,
                                          views: chartViewStats.data.views,
                                      },
                                      type: ResourceViewItemType.CHART,
                                  } satisfies ResourceViewChartItem,
                              ]
                            : []),
                    ]}
                    isLoading={isMovingChart || isContentActionLoading}
                    onClose={transferToSpaceModalHandlers.close}
                    onConfirm={async (newSpaceUuid) => {
                        if (!newSpaceUuid) {
                            throw new Error('No space uuid provided');
                        }

                        if (savedChart) {
                            await contentAction({
                                action: {
                                    type: 'move',
                                    targetSpaceUuid: newSpaceUuid,
                                },
                                item: {
                                    uuid: savedChart.uuid,
                                    contentType: ContentType.CHART,
                                    source: ChartSourceType.DBT_EXPLORE,
                                },
                            });
                        }
                        transferToSpaceModalHandlers.close();
                    }}
                />
            )}

            {isChangeExploreModalOpen &&
                savedChart &&
                projectUuid &&
                savedChart.tableName && (
                    <ChangeChartExploreModal
                        opened={isChangeExploreModalOpen}
                        onClose={changeExploreModalHandlers.close}
                        projectUuid={projectUuid}
                        chartUuid={savedChart.uuid}
                        currentExploreName={savedChart.tableName}
                        hasUnsavedChanges={hasUnsavedChanges && isEditMode}
                    />
                )}
        </TrackSection>
    );
};

export default SavedChartsHeader;
